import { setPickerFilters, setModalPickerShowClear, setModalPickerShowClearCount } from 'actions/ui';
import FilterMenu from 'components/asideRight/components/filterMenu';
import SearchField from 'components/general/searchField';
import { DomHelpers, Help, Loading, SystemText } from 'e4shared';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiPost } from 'services';

export const ModalPicker = ({ value, disabledUsers, onChange }) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = React.useState(false);
    const [loadingAll, setLoadingAll] = React.useState(false);
    const [filter, setFilter] = React.useState('');
    const [availablePage, setAvailablePage] = React.useState(1);
    const [selectedPage, setSelectedPage] = React.useState(1);
    const [allUsers, setAllUsers] = React.useState([]);
    const [filteredUsers, setFilteredUsers] = React.useState([]);
    const [selected, setSelected] = React.useState([]);
    const [available, setAvailable] = React.useState([]);
    const [ids, setIds] = React.useState(value || []);

    const { pickerFilters } = useSelector(state => state.ui);
    const { user } = useSelector(state => state.user);
    const allowCoaches = user.companySettings.development.allowCoaches;

    const { modalPickerShowClear, modalPickerShowClearCount } = useSelector(state => state.ui);
    const ratingsRange = useSelector(state => state.user.user.companySettings?.display?.ratingRange || 5);

    const maxRowsToDisplay = 10;

    let fields = [
        {
            property: 'manager_Ids',
            name: SystemText('General/Controls/ModalPicker/Filters/Manager'),
            type: 'list',
            nameProperty: 'list_Name',
        },
        {
            property: 'coach_Ids',
            name: SystemText('General/Controls/ModalPicker/Filters/Coach'),
            type: 'list',
            nameProperty: 'list_Name',
        },
        {
            property: 'role_Ids',
            name: SystemText('General/Controls/ModalPicker/Filters/Role'),
            type: 'list',
        },
        {
            property: 'job_Title',
            name: SystemText('General/Controls/ModalPicker/Filters/JobTitle'),
            type: 'text',
        },
        {
            property: 'department_Ids',
            name: SystemText('General/Controls/ModalPicker/Filters/Department'),
            type: 'list',
        },
        {
            property: 'business_Unit_Ids',
            name: SystemText('General/Controls/ModalPicker/Filters/BusinessUnit'),
            type: 'list',
        },
        {
            property: 'location_Ids',
            name: SystemText('General/Controls/ModalPicker/Filters/Location'),
            type: 'list',
        },
        {
            property: 'course_Status',
            name: SystemText('General/Controls/ModalPicker/Filters/CourseStatus'),
            type: 'courseStatus',
            value: [{ id: null, status: [] }],
        },
        {
            property: 'behaviour_Score',
            name: SystemText('General/Controls/ModalPicker/Filters/BehaviourScore'),
            type: 'behaviourScore',
            value: [{ id: null, range: [1, ratingsRange] }],
        },
    ];

    if (!allowCoaches) {
        fields = fields.filter(x => x.property !== 'coach_Ids');
    }

    const loadUsersFromFilters = () => {
        const searchFilters = [...pickerFilters].concat([{ property: 'search', value: filter }]);
        apiPost(
            'profile-filter',
            searchFilters,
            user.guid,
            response => {
                setFilteredUsers(response);
                setLoading(false);
            },
            null,
            'POST',
            'json'
        );
    };

    const loadAllUsers = () => {
        setLoadingAll(true);
        apiPost(
            'profile-filter',
            [],
            user.guid,
            response => {
                setAllUsers(response);
                setLoadingAll(false);
            },
            null,
            'POST',
            'json'
        );
    };

    React.useEffect(() => {
        setAvailablePage(1);
        setLoading(true);
        loadUsersFromFilters();
    }, [pickerFilters, filter]);

    React.useEffect(() => {
        const init = pickerFilters?.length ? pickerFilters : fields;
        dispatch(setPickerFilters(init));
        loadAllUsers();
        loadUsersFromFilters();
    }, []);

    React.useEffect(() => {
        onChange?.(ids);
    }, [ids]);

    React.useEffect(() => {
        setSelected(allUsers.filter(x => ids.some(id => id === x.id)));
        setAvailable(filteredUsers.filter(x => !ids.some(id => id === x.id)));
    }, [filteredUsers, allUsers, ids]);

    const selectUsers = e => {
        const btn = DomHelpers.getEventButton(e);
        const value = btn.value;
        if (value === 'all') {
            const newValues = ids.concat(filteredUsers.filter(x => !disabledUsers?.some(d => d.id === x.id)).map(x => x.id));
            setIds([...new Set(newValues)]);
        } else if (value === 'none') {
            const current = [...ids];
            setIds(current.filter(x => disabledUsers?.some(d => d.id === x)));
        } else {
            const id = parseInt(value);
            if (ids.some(x => x === id)) {
                setIds(ids.filter(x => x !== id));
            } else {
                setIds(ids.concat([id]));
            }
        }
    };

    const showMore = (list, page, incrementFunc) => {
        const showExtra = () => {
            incrementFunc(page + 1);
        };

        if (list.length < page * maxRowsToDisplay) {
            return null;
        }

        return (
            <>
                <div className="mt10">
                    {SystemText('General/Controls/ModalPicker/MoreRows', list.length - maxRowsToDisplay * page)}
                </div>
                <div className="mt10">
                    <button className="btn btn-xs" value="none" onClick={showExtra} data-testid="button-load-more">
                        {SystemText('General/Controls/ModalPicker/Buttons/LoadMore')}
                    </button>
                </div>
            </>
        );
    };

    const clearFilters = () => {
        dispatch(setPickerFilters(fields));
        dispatch(setModalPickerShowClear(false));
        dispatch(setModalPickerShowClearCount(0));
    };

    return (
        <>
            <SearchField
                placeholder={SystemText('General/Controls/ModalPicker/Fields/Search/Placeholder')}
                onChange={setFilter}
                className="modal-under-header"
                data-testid="search-all"
            />
            <div className="flex">
                <div className="flex pt10 pb10 pr10" style={{ flexBasis: '660px', borderRight: 'solid 1px #e5e5e5' }}>
                    <div className="mr20" style={{ flexBasis: '370px' }}>
                        <div className="flex flex-center">
                            <div className="bold flex-grow">Filter By</div>
                            {modalPickerShowClear && (
                                <div className="flex flex-center" style={{ marginRight: '15px' }}>
                                    <button
                                        data-testid="button-clear-filters"
                                        className="btn btn-link"
                                        onClick={clearFilters}
                                    >
                                        <span className="mr5">{SystemText('General/Controls/Filters/Clear')}</span>
                                    </button>
                                    <div className="flex-grow">
                                        <label className="label label-danger pull-left" style={{ marginBottom: '0' }}>
                                            {modalPickerShowClearCount}
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="filter-scroller">
                            <FilterMenu inline picker />
                        </div>
                    </div>
                    <div style={{ width: '250px' }}>
                        <b className="mr10 pl5">{SystemText('General/Controls/ModalPicker/AvailableUsers')}</b>
                        {loading ? (
                            <span className="loading-small loading-left">
                                <Loading />
                            </span>
                        ) : (
                            available.length > 0 && (
                                <>
                                    <div className="mt10 filter-scroller">
                                        <button
                                            className="btn btn-link btn-block btn-hover"
                                            value="all"
                                            onClick={selectUsers}
                                            data-testid="button-add-all-users"
                                        >
                                            <i className="fa fa-solid fa-plus-circle mr10" />
                                            {SystemText('General/Controls/ModalPicker/Buttons/AddAll')}
                                        </button>
                                        <>
                                            {available.map((d, i) => {
                                                if (i + 1 > maxRowsToDisplay * availablePage) {
                                                    return null;
                                                }

                                                const disabledMessage = disabledUsers?.find(x => x.id == d.id)?.message;

                                                return (
                                                    <button
                                                        key={`user-add-${d.id}`}
                                                        className="btn btn-link btn-block btn-hover flex flex-row"
                                                        value={d.id}
                                                        disabled={disabledMessage}
                                                        onClick={selectUsers}
                                                        data-testid="button-add-user"
                                                    >
                                                        <i className="fa fa-plus-circle mr10" style={{ marginTop: '3px' }} />
                                                        <div>
                                                            {d.first_Name} {d.last_Name}
                                                            {disabledMessage && (
                                                                <Help id={`disabled-help-${d.id}`}>{disabledMessage}</Help>
                                                            )}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </>
                                        {showMore(available, availablePage, setAvailablePage)}
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </div>

                <div className="pt10 pb10 pl10" style={{ flexBasis: '260px' }}>
                    <div style={{}} className="pl5 mb10 flex flex-center">
                        <b className="mr10">{SystemText('General/Controls/ModalPicker/SelectedUsers')}</b>
                        <span className={`label label-${ids.length > 0 ? 'success' : 'danger'}`}>{ids.length}</span>
                    </div>
                    {ids.length > 0 && (
                        <>
                            <div className="mt10 filter-scroller">
                                <button
                                    className="btn btn-link btn-block btn-hover"
                                    value="none"
                                    onClick={selectUsers}
                                    data-testid="button-remove-all-users"
                                >
                                    <i className="fa fa-solid fa-times-circle mr10" />
                                    {SystemText('General/Controls/ModalPicker/Buttons/RemoveAll')}
                                </button>
                                <>
                                    {loadingAll ? (
                                        <span className="loading-small loading-left">
                                            <Loading />
                                        </span>
                                    ) : (
                                        selected.map((d, i) => {
                                            if (i + 1 > maxRowsToDisplay * selectedPage) {
                                                return null;
                                            }

                                            const disabledMessage = disabledUsers?.find(x => x.id == d.id)?.message;

                                            return (
                                                <button
                                                    key={`user-${d.id}`}
                                                    disabled={disabledMessage}
                                                    className="btn btn-link btn-block btn-hover flex flex-row"
                                                    value={d.id}
                                                    onClick={selectUsers}
                                                    data-testid="button-remove-user"
                                                >
                                                    <i className="fa fa-times-circle mr10" style={{ marginTop: '3px' }} />
                                                    {d.first_Name} {d.last_Name}
                                                    {disabledMessage && (
                                                        <Help id={`disabled-help-${d.id}`}>{disabledMessage}</Help>
                                                    )}
                                                </button>
                                            );
                                        })
                                    )}
                                </>
                                {showMore(selected, selectedPage, setSelectedPage)}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};
